/**
 * The revenue-management route table (design C12 §5.5).
 *
 * A forecast and a displacement evaluation each get a child path rather than a
 * drawer, because both are things people link each other to: "look at the
 * variance on version 7" and "DSP-2026-000042 nets out negative" have to
 * survive being pasted into an email, and a drawer has no URL.
 */

import type { RouteRecordRaw } from 'vue-router'

import { RVM_PERMISSIONS } from '@/modules/rvm/permissions'

export const RVM_ROUTES: RouteRecordRaw[] = [
  // C13 §B1: the two configuration screens §5.5 omitted. `rvm.settings.manage`
  // and `rvm.dimension.manage` were declared, seeded onto roles, and reached no
  // UI at all — so a property could not set its materiality bands or map a
  // segment from the tenant app. They come FIRST in the table but LAST in the
  // section nav, because they are what a property sets up once and revisits by
  // season, not what a revenue manager opens on a Monday morning.
  {
    path: '/rvm/settings',
    name: 'rvm-settings',
    component: () => import('@/modules/rvm/SettingsView.vue'),
    meta: { titleKey: 'rvm.routeTitles.settings', permission: RVM_PERMISSIONS.settingsRead },
  },
  {
    path: '/rvm/dimensions',
    name: 'rvm-dimensions',
    component: () => import('@/modules/rvm/DimensionsView.vue'),
    meta: { titleKey: 'rvm.routeTitles.dimensions', permission: RVM_PERMISSIONS.dimensionRead },
  },
  {
    path: '/rvm/on-the-books',
    name: 'rvm-on-the-books',
    component: () => import('@/modules/rvm/OnTheBooksView.vue'),
    meta: {
      titleKey: 'rvm.routeTitles.onTheBooks',
      permission: RVM_PERMISSIONS.snapshotRead,
    },
  },
  {
    path: '/rvm/pace',
    name: 'rvm-pace',
    component: () => import('@/modules/rvm/PaceView.vue'),
    meta: { titleKey: 'rvm.routeTitles.pace', permission: RVM_PERMISSIONS.paceRead },
  },
  {
    path: '/rvm/commercial-report',
    name: 'rvm-commercial-report',
    component: () => import('@/modules/rvm/CommercialReportView.vue'),
    meta: { titleKey: 'rvm.routeTitles.commercial', permission: RVM_PERMISSIONS.paceRead },
  },
  {
    path: '/rvm/forecasts',
    name: 'rvm-forecasts',
    component: () => import('@/modules/rvm/ForecastsListView.vue'),
    meta: { titleKey: 'rvm.routeTitles.forecasts', permission: RVM_PERMISSIONS.forecastRead },
  },
  {
    path: '/rvm/forecasts/:id',
    name: 'rvm-forecast',
    component: () => import('@/modules/rvm/ForecastView.vue'),
    meta: { titleKey: 'rvm.routeTitles.forecast', permission: RVM_PERMISSIONS.forecastRead },
  },
  {
    path: '/rvm/accuracy',
    name: 'rvm-accuracy',
    component: () => import('@/modules/rvm/AccuracyView.vue'),
    meta: { titleKey: 'rvm.routeTitles.accuracy', permission: RVM_PERMISSIONS.accuracyRead },
  },
  {
    path: '/rvm/displacements',
    name: 'rvm-displacements',
    component: () => import('@/modules/rvm/DisplacementsListView.vue'),
    meta: {
      titleKey: 'rvm.routeTitles.displacements',
      permission: RVM_PERMISSIONS.displacementRead,
    },
  },
  {
    path: '/rvm/displacements/:id',
    name: 'rvm-displacement',
    component: () => import('@/modules/rvm/DisplacementView.vue'),
    meta: {
      titleKey: 'rvm.routeTitles.displacement',
      permission: RVM_PERMISSIONS.displacementRead,
    },
  },
  {
    path: '/rvm/hurdles',
    name: 'rvm-hurdles',
    component: () => import('@/modules/rvm/HurdlesView.vue'),
    meta: { titleKey: 'rvm.routeTitles.hurdles', permission: RVM_PERMISSIONS.hurdleRead },
  },
  {
    path: '/rvm/competitor-rates',
    name: 'rvm-competitor-rates',
    component: () => import('@/modules/rvm/CompetitorRatesView.vue'),
    meta: {
      titleKey: 'rvm.routeTitles.competitorRates',
      permission: RVM_PERMISSIONS.competitorRead,
    },
  },
]
